import { API_ERROR_CODES, parseApiError } from "@/lib/api/api-error";

export interface SignupFormValues {
  companyName: string;
  contactName: string;
  email: string;
  password: string;
}

export const SIGNUP_FIELD_LIMITS = {
  companyName: 255,
  contactName: 200,
  email: 255,
  password: 128,
  passwordMin: 8,
} as const;

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function isSignupFormValid(form: SignupFormValues): boolean {
  const companyName = form.companyName.trim();
  const contactName = form.contactName.trim();
  const email = form.email.trim();

  return (
    companyName.length >= 1 &&
    companyName.length <= SIGNUP_FIELD_LIMITS.companyName &&
    contactName.length >= 1 &&
    contactName.length <= SIGNUP_FIELD_LIMITS.contactName &&
    isValidEmail(email) &&
    email.length <= SIGNUP_FIELD_LIMITS.email &&
    form.password.length >= SIGNUP_FIELD_LIMITS.passwordMin &&
    form.password.length <= SIGNUP_FIELD_LIMITS.password
  );
}

export function toRegisterUserRequest(form: SignupFormValues) {
  return {
    company_name: form.companyName.trim(),
    contact_name: form.contactName.trim(),
    email: form.email.trim(),
    password: form.password,
    terms_accepted: true,
  };
}

export type RegisterErrorResult = {
  message: string;
  duplicateEmail: boolean;
};

const REGISTER_MESSAGES = {
  network:
    "We couldn't reach the server. Check your connection and try again.",
  duplicateEmail: "An account with this email already exists.",
  duplicateEmailFallback:
    "This email is already registered. Try to log in instead.",
  validation:
    "Please check your details and try again. Make sure all required fields are filled in correctly.",
  server:
    "Something went wrong on our side. Please try again in a few minutes.",
  generic: "Something went wrong. Please try again later.",
} as const;

const POST_REGISTER_LOGIN_MESSAGES = {
  network:
    "Your account was created, but we couldn't sign you in. Check your connection and log in manually.",
  invalidCredentials:
    "Your account was created, but automatic sign-in failed. Please log in with your email and password.",
  accountNotActive:
    "Your account was created but isn't active yet. Please log in once your account is ready.",
  badRequest:
    "Your account was created, but sign-in failed. Please log in manually.",
  generic:
    "Your account was created, but we couldn't sign you in automatically. Please log in manually.",
} as const;

export function getRegisterErrorMessage(err: unknown): RegisterErrorResult {
  const { status, code, isNetworkError } = parseApiError(err);

  if (isNetworkError) {
    return { message: REGISTER_MESSAGES.network, duplicateEmail: false };
  }

  if (
    status === 409 &&
    code === API_ERROR_CODES.duplicateEmail
  ) {
    return {
      message: REGISTER_MESSAGES.duplicateEmail,
      duplicateEmail: true,
    };
  }

  if (status === 409) {
    return {
      message: REGISTER_MESSAGES.duplicateEmailFallback,
      duplicateEmail: false,
    };
  }

  if (
    status === 500 ||
    code === API_ERROR_CODES.internalServerError
  ) {
    return { message: REGISTER_MESSAGES.server, duplicateEmail: false };
  }

  if (status === 400) {
    return { message: REGISTER_MESSAGES.validation, duplicateEmail: false };
  }

  return { message: REGISTER_MESSAGES.generic, duplicateEmail: false };
}

export function getPostRegisterLoginErrorMessage(err: unknown): string {
  const { status, code, isNetworkError } = parseApiError(err);

  if (isNetworkError) {
    return POST_REGISTER_LOGIN_MESSAGES.network;
  }

  if (
    status === 401 ||
    code === API_ERROR_CODES.invalidCredentials
  ) {
    return POST_REGISTER_LOGIN_MESSAGES.invalidCredentials;
  }

  if (
    status === 403 ||
    code === API_ERROR_CODES.accountNotActive
  ) {
    return POST_REGISTER_LOGIN_MESSAGES.accountNotActive;
  }

  if (status === 400) {
    return POST_REGISTER_LOGIN_MESSAGES.badRequest;
  }

  return POST_REGISTER_LOGIN_MESSAGES.generic;
}
